import { ColorBaseGreenCompliant } from '@huntington/honeycomb-design';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Stack, styled, Typography } from '@mui/material';
import { useMemo, type PropsWithChildren } from 'react';
import {
	PASSWORD_REQUIREMENT_LENGTH,
	PASSWORD_REQUIREMENT_LOWERCASE,
	PASSWORD_REQUIREMENT_MAX_LENGTH,
	PASSWORD_REQUIREMENT_NUMBER,
	PASSWORD_REQUIREMENT_SPACES,
	PASSWORD_REQUIREMENT_UPPERCASE,
	PASSWORD_REQUIREMENTS,
} from '../../constants/messages';

const uppercaseRegex = new RegExp(/[A-Z]/);
const lowercaseRegex = new RegExp(/[a-z]/);
const numberRegex = new RegExp(/\d/);
const spaceRegex = new RegExp(/\s/);
export const validatePassword = (password: string) => {
	const requirements = {
		allValid: false,
		length: password.length > 8,
		maxLength: password.length <= 16,
		lowercase: lowercaseRegex.test(password),
		number: numberRegex.test(password),
		space: !spaceRegex.test(password),
		uppercase: uppercaseRegex.test(password),
	};
	if (
		requirements.length &&
		requirements.maxLength &&
		requirements.lowercase &&
		requirements.number &&
		requirements.space &&
		requirements.uppercase
	) {
		requirements.allValid = true;
	}
	return requirements;
};

export interface PasswordRequirementsProps {
	password: string;
}

export const PasswordRequirements = ({ password }: PasswordRequirementsProps) => {
	const isValid = useMemo(() => validatePassword(password), [password]);

	return (
		<Stack>
			{!isValid.allValid && (
				<>
					<Typography sx={{ paddingTop: '1rem', fontWeight: 'bold', fontSize: '0.875rem' }}>
						{PASSWORD_REQUIREMENTS}
					</Typography>
					<StyledRequirement role="alert" aria-live="polite">
						{isValid.length ? (
							<StyledPassIcon titleAccess="Pass icon" />
						) : (
							<StyledFailIcon titleAccess="Fail icon" />
						)}
						{PASSWORD_REQUIREMENT_LENGTH}
					</StyledRequirement>
					<StyledRequirement role="alert" aria-live="polite">
						{isValid.maxLength ? (
							<StyledPassIcon titleAccess="Pass icon" />
						) : (
							<StyledFailIcon titleAccess="Fail icon" />
						)}
						{PASSWORD_REQUIREMENT_MAX_LENGTH}
					</StyledRequirement>
					<StyledRequirement role="alert" aria-live="polite">
						{isValid.uppercase ? (
							<StyledPassIcon titleAccess="Pass icon" />
						) : (
							<StyledFailIcon titleAccess="Fail icon" />
						)}
						{PASSWORD_REQUIREMENT_UPPERCASE}
					</StyledRequirement>
					<StyledRequirement role="alert" aria-live="polite">
						{isValid.lowercase ? (
							<StyledPassIcon titleAccess="Pass icon" />
						) : (
							<StyledFailIcon titleAccess="Fail icon" />
						)}
						{PASSWORD_REQUIREMENT_LOWERCASE}
					</StyledRequirement>
					<StyledRequirement role="alert" aria-live="polite">
						{isValid.number ? (
							<StyledPassIcon titleAccess="Pass icon" />
						) : (
							<StyledFailIcon titleAccess="Fail icon" />
						)}
						{PASSWORD_REQUIREMENT_NUMBER}
					</StyledRequirement>
					<StyledRequirement role="alert" aria-live="polite">
						{isValid.space ? (
							<StyledPassIcon titleAccess="Pass icon" />
						) : (
							<StyledFailIcon titleAccess="Fail icon" />
						)}
						{PASSWORD_REQUIREMENT_SPACES}
					</StyledRequirement>
				</>
			)}
		</Stack>
	);
};

export const SuccessMessage = ({ children }: PropsWithChildren) => (
	<StyledRequirement sx={{ fontWeight: 'bold' }}>
		<StyledPassIcon titleAccess="Pass icon" />
		{children}
	</StyledRequirement>
);

const StyledRequirement = styled(Typography)({
	alignItems: 'center',
	display: 'flex',
	fontSize: '0.875rem',
	paddingTop: '0.5rem',
});

const StyledPassIcon = styled(CheckCircleIcon)({
	color: ColorBaseGreenCompliant,
	height: '1.125rem',
	marginRight: '0.5rem',
	width: '1.125rem',
});

const StyledFailIcon = styled(RemoveCircleOutlineIcon)({
	height: '1.125rem',
	marginRight: '0.5rem',
	width: '1.125rem',
});
